
import { useState } from 'react';
import { CheckCircle, Clock, DollarSign, MapPin, Users, Briefcase } from 'lucide-react';

interface JobCardProps {
  job: {
    id: string;
    title: string;
    department: string;
    location: string;
    remote: boolean;
    type: string;
    salary: string;
    posted: string;
    description: string;
    // Note: employerId is intentionally not displayed
  };
  isSelected: boolean;
  onSelect: (id: string) => void;
}

const JobCard = ({ job, isSelected, onSelect }: JobCardProps) => {
  const [showDetails, setShowDetails] = useState(false);

  // Format salary to ensure it has Rupee symbol
  const formattedSalary = job.salary.includes('₹') 
    ? job.salary 
    : `₹${job.salary.replace(/^\$/, '')}`;

  return (
    <div
      className={`border rounded-xl transition-all duration-300 hover-scale overflow-hidden ${
        isSelected
          ? 'border-apple-blue bg-blue-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="text-lg font-semibold text-apple-text">{job.title}</h3>
            <div className="text-apple-text-secondary">{job.department}</div>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => onSelect(job.id)}
              className={`w-6 h-6 rounded-full border ${
                isSelected
                  ? 'bg-apple-blue border-apple-blue text-white'
                  : 'border-gray-300'
              } flex items-center justify-center transition-all duration-200`}
            >
              {isSelected && <CheckCircle size={16} />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center text-sm text-apple-text-secondary">
            <MapPin size={16} className="mr-1.5" />
            <span>{job.location}</span>
            {job.remote && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-gray-100 rounded text-xs">
                Remote
              </span>
            )}
          </div>
          <div className="flex items-center text-sm text-apple-text-secondary">
            <Briefcase size={16} className="mr-1.5" />
            <span>{job.type}</span>
          </div>
          <div className="flex items-center text-sm text-apple-text-secondary">
            <DollarSign size={16} className="mr-1.5" />
            <span>{formattedSalary}</span>
          </div>
          <div className="flex items-center text-sm text-apple-text-secondary">
            <Clock size={16} className="mr-1.5" />
            <span>Posted {job.posted}</span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-apple-blue font-medium hover:underline transition-colors"
          >
            {showDetails ? 'Hide Details' : 'View Details'}
          </button>
          
          {isSelected && (
            <div className="text-sm text-apple-blue font-medium flex items-center">
              <CheckCircle size={16} className="mr-1.5" />
              Selected
            </div>
          )}
        </div>

        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-apple-text">
            <p className="mb-3">{job.description}</p>
            <div className="flex items-center text-apple-text-secondary">
              <Users size={16} className="mr-1.5" />
              <span>23 applicants</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobCard;
